import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OtpMedium } from '../../types';
import './PhoneStep.css';

interface PhoneStepProps {
  cvrt: string;
  phoneLastDigits: string | null;
  loading: boolean;
  error: string | null;
  senderName: string | null;
  onRequestOtp: (cvrt: string, phoneNumber: string, medium: OtpMedium) => Promise<boolean>;
  onNext: () => void;
}

const PhoneStep: React.FC<PhoneStepProps> = ({
  cvrt,
  phoneLastDigits,
  loading,
  error,
  senderName,
  onRequestOtp,
  onNext,
}) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [medium, setMedium] = useState<OtpMedium>('sms');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      return;
    }

    // Reset the OTP sent state in case of retry
    setOtpSent(false);

    try {
      const success = await onRequestOtp(cvrt, phoneNumber.trim(), medium);
      if (success) {
        setOtpSent(true);
        // Auto-advance to next step after successful OTP send
        setTimeout(() => {
          onNext();
        }, 2000);
      }
      // If not successful, error will be displayed via the error prop
    } catch (err) {
      // Error handling is done in the parent component
      console.error('Error requesting OTP:', err);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    setPhoneNumber(value);
  };

  if (otpSent && !error) {
    return (
      <div className="phone-step">
        <div className="card">
          <div className="card-header">
            <h2>{t('phone.title')}</h2>
          </div>
          <div className="card-body">
            <div className="success-container">
              <div className="alert alert-success">
                <div className="success-icon">✅</div>
                <p>
                  {medium === 'voice'
                    ? t('phone.successVoice')
                    : t('phone.successSms')}
                </p>
                {medium === 'voice' && senderName && (
                  <p className="sender-info">
                    <strong>{t('phone.expectedCallFrom')}:</strong> {senderName}
                  </p>
                )}
                <p>{t('phone.redirecting')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-step">
      <div className="card">
        <div className="card-header">
          <h2>{t('phone.title')}</h2>
          <p>{t('phone.description')}</p>
          {phoneLastDigits && (
            <div className="phone-hint">
              <p className="hint-text">
                {t('phone.phoneEndingWith')}: <strong>****{phoneLastDigits}</strong>
              </p>
            </div>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="phone-form">
            <div className="form-group">
              <label htmlFor="phoneNumber" className="form-label">
                {t('phone.phoneLabel')}
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder={t('phone.phonePlaceholder') as string}
                className="form-input"
                required
                disabled={loading}
                maxLength={20}
              />
              <div className="form-help">
                {t('phone.phoneHelp')}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('phone.mediumLabel')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="medium"
                    value="sms"
                    checked={medium === 'sms'}
                    onChange={(e) => setMedium(e.target.value as OtpMedium)}
                    disabled={loading}
                  />
                  <span>{t('phone.mediumSms')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="medium"
                    value="voice"
                    checked={medium === 'voice'}
                    onChange={(e) => setMedium(e.target.value as OtpMedium)}
                    disabled={loading}
                  />
                  <span>{t('phone.mediumVoice')}</span>
                </label>
              </div>
            </div>

            {error && (
              <div className="alert alert-error">
                <strong>{t('common.error')}</strong>
                <p>{error}</p>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary btn-large"
                disabled={loading || !phoneNumber.trim()}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('phone.sending')}
                  </>
                ) : (
                  t('phone.sendOtp')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PhoneStep;